/**
 * Per-agent settings API service (SPEC §2.6 control surface).
 */

import type {
  AgentDomain,
  AgentSettings,
  AgentSettingsUpdate,
  AgentsListResponse,
  EnablementBoard,
} from '../types/agentSettings';
import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * List all agents' effective settings plus the fixed platform policy.
 */
export async function listAgentSettings(): Promise<AgentsListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/agents`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<AgentsListResponse>(response);
}

/**
 * Update one agent's settings (tenant-scoped override). The server enforces
 * the platform floors and returns 422 on any attempt to cross them.
 */
export async function updateAgentSettings(
  domain: AgentDomain,
  update: AgentSettingsUpdate
): Promise<AgentSettings> {
  const response = await fetch(
    `${API_BASE_URL}/api/agents/${domain}/settings`,
    {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(update),
    }
  );
  return handleResponse<AgentSettings>(response);
}

/**
 * The agent-enablement switchboard in dependency order, with the tenant's
 * Tier-1 autonomy master switch.
 */
export async function getEnablementBoard(): Promise<EnablementBoard> {
  const response = await fetch(`${API_BASE_URL}/api/agents/enablement`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<EnablementBoard>(response);
}

/**
 * Update the tenant-level autonomy switches (partial update — omitted flags
 * are left unchanged). Never widens the platform tier floors.
 */
export async function updateMasterSwitch(switches: {
  agents_enabled?: boolean;
  tier1_auto_enabled?: boolean;
}): Promise<EnablementBoard> {
  const response = await fetch(
    `${API_BASE_URL}/api/agents/enablement/master`,
    {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(switches),
    }
  );
  return handleResponse<EnablementBoard>(response);
}

export interface SettingsAuditEntry {
  id: string;
  surface: string;
  object_key: string;
  event: string;
  changed_by: string | null;
  /** spec #47 D2 (soul build) — the ROLE the author acted in, and the
   * proposal that approved the change. Optional because they do not exist
   * yet: until they do, a change by anyone but the reader degrades to
   * `source unrecorded` rather than guessing (P5). */
  author_role?: 'tenant' | 'operator' | null;
  source_proposal_id?: string | null;
  /** Field-level old->new diff. Never contains secret material. */
  changes: Record<string, { old: unknown; new: unknown }>;
  created_at: string | null;
}

export interface SettingsAuditListResponse {
  entries: SettingsAuditEntry[];
  total: number;
  has_more: boolean;
}

/**
 * Tenant-scoped settings change history, newest first.
 */
export async function listSettingsAudit(
  limit = 20,
  offset = 0
): Promise<SettingsAuditListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const response = await fetch(
    `${API_BASE_URL}/api/settings/audit?${params}`,
    {
      method: 'GET',
      headers: createHeaders(),
    }
  );
  return handleResponse<SettingsAuditListResponse>(response);
}
